import {
  BasketContent,
  Prestation,
  PrestationReference,
  Universe,
  PrestationWithExtraData,
  PricesByZone,
} from '../types/commons';

import { formatDuration } from './formatting';
import { isHourlyBased } from './universes';

export const findPrestation = (
  reference: PrestationReference,
  universe?: Universe,
): PrestationWithExtraData | null => {
  if (!universe) return null;
  for (let i = 0; i < universe.categories.length; i += 1) {
    const category = universe.categories[i];
    for (let j = 0; j < category.subcategories.length; j += 1) {
      const subcategory = category.subcategories[j];
      for (let k = 0; k < subcategory.prestations.length; k += 1) {
        const prestation = subcategory.prestations[k];
        if (prestation.reference === reference) {
          return {
            ...prestation,
            subcategory: {
              reference: subcategory.reference,
              title: subcategory.title,
              category: {
                reference: category.reference,
                title: category.title,
              },
            },
          };
        }
      }
    }
  }

  return null;
};

export const findUniverse = (
  prestationReference: PrestationReference,
  universes: Universe[],
): Universe | undefined => {
  const foundUniverse = universes.find((universe: Universe) => findPrestation(prestationReference, universe));
  return foundUniverse;
};

export const getDurationFormatted = (duration: number, universe: Universe) =>
  isHourlyBased(universe) && duration ? formatDuration(duration) : '';

export const getPrestationsTitles = (prestations: (Prestation | undefined)[], durationText?: string) => {
  const joinedPrestations = prestations.map((prestation) => prestation?.title ?? '?').join(' + ');

  return durationText ? `${joinedPrestations} ${durationText}` : joinedPrestations;
};

export const createBasketContentFromPrestations = (prestations: PrestationReference[], universe: Universe) => {
  const basketContent: BasketContent = [];

  prestations.forEach((prestationRef) => {
    const prestation = findPrestation(prestationRef, universe);

    if (prestation) {
      const {
        subcategory: {
          category: { reference },
        },
      } = prestation;

      const alreadyAddedCategory = basketContent.find((c) => c.reference === reference);

      if (alreadyAddedCategory) {
        alreadyAddedCategory.prestations.push(prestationRef);
      } else {
        basketContent.push({
          reference,
          prestations: [prestationRef],
        });
      }
    }
  });

  return basketContent;
};

export const isThaiMassagePrestation = (reference: PrestationReference) =>
  !!reference && reference.startsWith('thai_massage');

export const minimumPrice = (priceList: PricesByZone): number =>
  Array.isArray(priceList) ? Math.min(...priceList.map((price: number[]) => price[1])) : priceList;

export const getAllPrestationsForCategory = (categoryReference: string, universe: Universe) => {
  const category = universe.categories.find((cat) => cat.reference === categoryReference);
  if (!category) return [];
  return category.subcategories.reduce((acc: Prestation[], subcat) => acc.concat(subcat.prestations), []);
};
